import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {
  BufferConditionKeys,
  MELTING_TEMP_PREFERENCES_DEFAULTS,
  MeltingTempPreferences,
  NucleotideType,
  MeltingTempMethod,
} from 'biojs/BioConstants';
import {IntermediateValueControlledNumericInput} from 'ui-library/inputs/ControlledNumericInput';

interface MeltingTempOptionProps {
  optionsHaveValidationError: {[key in BufferConditionKeys]: boolean};
  meltingTempOptions: MeltingTempPreferences;
  nucleotideType: NucleotideType;
  optionKey: BufferConditionKeys;
  setMeltingTempOptions: (prefs: MeltingTempPreferences) => void;
  setOptionsHaveValidationError: (errors: {[key in BufferConditionKeys]: boolean}) => void;
}

const getOptionKeyLabels = (nucleotideType: NucleotideType): {[key in BufferConditionKeys]: JSX.Element} => ({
  dnaConc: <label>{nucleotideType}</label>,
  monovalentConc: (
    <label>
      Na<sup>+</sup>/K<sup>+</sup>
    </label>
  ),
  divalentConc: (
    <label>
      Mg<sup>2+</sup>
    </label>
  ),
  dntpConc: <label>dNTP</label>,
});

const OPTION_KEY_UNITS: {[key in BufferConditionKeys]: string} = {
  dnaConc: 'nM',
  monovalentConc: 'mM',
  divalentConc: 'mM',
  dntpConc: 'mM',
};

/* Determine a minimum, exclusive value for some numeric inputs to guard against math errors (e.g. taking the log of 0)
 * during melting temperature calculations.
 */
function getExclusiveMin(
  optionKey: BufferConditionKeys,
  meltingTempOptions: MeltingTempPreferences
): number | undefined {
  if (optionKey === 'dnaConc') {
    return 0;
  } else if (
    [MeltingTempMethod.xia, MeltingTempMethod.thermo].includes(meltingTempOptions.method) &&
    optionKey === 'monovalentConc'
  ) {
    // These methods only support monovalent concentration. Primer3 is more complex and harder to guard against because
    // it takes into account divalent concentration and performs some additional math.
    return 0;
  }
  return undefined;
}

export default function MeltingTempOption({
  optionsHaveValidationError,
  meltingTempOptions,
  nucleotideType,
  optionKey,
  setMeltingTempOptions,
  setOptionsHaveValidationError,
}: MeltingTempOptionProps): JSX.Element {
  return (
    <>
      <div className={classNames(['col col-md-2', css(styles.label)])}>
        {getOptionKeyLabels(nucleotideType)[optionKey]}
      </div>
      <div className="col col-md-3">
        <div className="input-group">
          <IntermediateValueControlledNumericInput
            value={meltingTempOptions[optionKey]}
            onChange={(e) => {
              if (!e.target.isValid) {
                setOptionsHaveValidationError({...optionsHaveValidationError, [optionKey]: true});
                setMeltingTempOptions({...meltingTempOptions, [optionKey]: null});
              } else {
                setOptionsHaveValidationError({...optionsHaveValidationError, [optionKey]: false});
                setMeltingTempOptions({...meltingTempOptions, [optionKey]: e.target.numericValue});
              }
            }}
            placeholder={MELTING_TEMP_PREFERENCES_DEFAULTS[optionKey].toString()}
            min={getExclusiveMin(optionKey, meltingTempOptions)}
            minIsInclusive={false}
          />
          <span className="units input-group-addon">{OPTION_KEY_UNITS[optionKey]}</span>
        </div>
      </div>
    </>
  );
}

const styles = StyleSheet.create({
  label: {
    marginTop: '4px',
    marginBottom: '2px',
  },
});
